import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { useBlocker, useNavigate } from 'react-router'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { useT } from '@/i18n'
import { useStores } from '@/stores'

/**
 * The shell's single router blocker (a router supports only one) plus the
 * shared unsaved-changes prompt «Данные были изменены. Сохранить изменения?»:
 * Да (save, then go on) · Нет (go on without saving) · Отмена (stay).
 */
export const LeaveGuardHost = observer(function LeaveGuardHost() {
  const t = useT()
  const { leaveGuard } = useStores()
  const navigate = useNavigate()
  const blocker = useBlocker(({ currentLocation, nextLocation }) => leaveGuard.shouldBlock(currentLocation, nextLocation))
  const blockerRef = useRef(blocker)
  useEffect(() => {
    blockerRef.current = blocker
  })

  useEffect(() => {
    if (blocker.state !== 'blocked') return
    let answered = false
    // The ref is synced by the effect above, which runs first.
    const target = blockerRef.current.location
    void leaveGuard.ask(leaveGuard.canSaveAll).then(async (choice) => {
      answered = true
      if (choice === 'save' && !(await leaveGuard.saveAll())) {
        // A form did not save: stay on it with its errors.
        if (blockerRef.current.state === 'blocked') blockerRef.current.reset()
        return
      }
      const current = blockerRef.current
      if (choice === 'stay') {
        if (current.state === 'blocked') current.reset()
        return
      }
      leaveGuard.releaseAll()
      if (current.state === 'blocked') current.proceed()
      // Saving a new record may itself move the page (to the created record); still go where the user asked.
      else if (choice === 'save' && target) navigate(target)
    })
    return () => {
      // The navigation was settled elsewhere; close a prompt nobody will answer.
      if (!answered) leaveGuard.pending?.resolve('stay')
    }
  }, [blocker.state, leaveGuard, navigate])

  const pending = leaveGuard.pending
  const answer = (choice: 'save' | 'leave' | 'stay') => pending?.resolve(choice)

  return (
    <Dialog
      open={Boolean(pending)}
      onClose={() => answer('stay')}
      title={t('layout.leaveGuard.title')}
      description={pending?.canSave ? t('layout.leaveGuard.text') : t('layout.leaveGuard.textNoSave')}
      footer={
        <>
          <Button variant="ghost" className="mr-auto" onClick={() => answer('stay')}>
            {t('layout.leaveGuard.cancel')}
          </Button>
          <Button variant="secondary" onClick={() => answer('leave')} data-autofocus={pending?.canSave ? undefined : true}>
            {pending?.canSave ? t('layout.leaveGuard.no') : t('layout.leaveGuard.leave')}
          </Button>
          {pending?.canSave && (
            <Button variant="primary" onClick={() => answer('save')} data-autofocus>
              {t('layout.leaveGuard.yes')}
            </Button>
          )}
        </>
      }
    />
  )
})
